import { fromPairs } from 'lodash';
import { useMemo } from 'react';
import { Category } from 'api/timesheets/categories';
import {
  BooleanField,
  FieldType,
  FilterableField,
  SingleSelectField,
  SingleUserField,
  SourceType,
} from 'components/organisms/tasks/fields';
import { useNativeActiveOrInvitedUsersQuery } from 'queries/members';
import { useVendorRolesQuery } from 'queries/roles';
import { getRandomColor } from 'utils/get-random-color';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';

const FIELD_ID_USER = -2001;
const FIELD_ID_ROLE = -2002;
const FIELD_ID_BILLABLE = -2003;
const FIELD_ID_CATEGORY = -2004;

export const useFilterFields = ({
  timesheetCategories,
}: {
  timesheetCategories: Category[];
}): {
  fields: FilterableField[];
  isFieldsLoading: boolean;
} => {
  const { data: nativeUsers = [], isLoading: isUserLoading } =
    useNativeActiveOrInvitedUsersQuery();
  const { data: roles = [], isLoading: isRoleLoading } = useVendorRolesQuery();
  const roleOptions = useMemo(
    () =>
      roles.map(({ roleId, roleName }) => ({
        label: roleName,
        value: roleId,
        color: getRandomColor(roleId),
        active: true,
      })),
    [roles],
  );
  const userField: SingleUserField = useMemo(
    () => ({
      fieldId: FIELD_ID_USER,
      fieldName: 'userId',
      fieldLabel: 'Team member',
      type: FieldType.SingleUser,
      private: false,
      userCreated: false,
      metaData: {
        users: {
          by_user_id: fromPairs(nativeUsers.map((x) => [x.userId, x])),
          active_user_ids: nativeUsers.map((x) => x.userId),
          by_project_id: {},
        },
      },
      sourceType: SourceType.User,
    }),
    [nativeUsers],
  );
  const roleField: SingleSelectField = useMemo(
    () => ({
      fieldId: FIELD_ID_ROLE,
      fieldName: 'role',
      fieldLabel: 'Role',
      type: FieldType.SingleSelect,
      private: false,
      userCreated: false,
      metaData: {
        options: roleOptions,
      },
      sourceType: SourceType.User,
    }),
    [roleOptions],
  );
  const billableField: BooleanField = useMemo(
    () => ({
      fieldId: FIELD_ID_BILLABLE,
      fieldName: 'billable',
      fieldLabel: 'Billable',
      type: FieldType.Boolean,
      private: false,
      userCreated: false,
      sourceType: SourceType.User,
    }),
    [],
  );
  const categoryField: SingleSelectField = useMemo(
    () => ({
      fieldId: FIELD_ID_CATEGORY,
      fieldName: 'category',
      fieldLabel: 'Category',
      type: FieldType.SingleSelect,
      private: false,
      userCreated: false,
      metaData: {
        options: timesheetCategories.map(({ id, name, deleted }) => ({
          value: id,
          label: name,
          color: getRandomColor(id),
          extra: { deleted },
          active: true,
        })),
      },
      sourceType: SourceType.User,
    }),
    [timesheetCategories],
  );
  const { loading: projectFieldsLoading, fields: projectFields } =
    useProjectFieldsAsFilterableFields();

  return {
    fields: useMemo(() => {
      const updatedCustomFields = projectFields
        .map((cf) =>
          cf.fieldName === 'status'
            ? { ...cf, fieldLabel: 'Project status' }
            : { ...cf },
        )
        .filter(
          ({ fieldName }) =>
            fieldName !== 'teamMembers' && fieldName !== 'partnerCompany',
        );

      return [
        userField,
        roleField,
        billableField,
        categoryField,
        ...updatedCustomFields,
      ].filter((x) => Boolean(x)) as FilterableField[];
    }, [userField, roleField, billableField, categoryField, projectFields]),
    isFieldsLoading: isUserLoading || isRoleLoading || projectFieldsLoading,
  };
};
